import { Component, OnDestroy, OnInit } from '@angular/core';
import { CompositeProductModel, CompositeProductType, HxProductInfoService, HxUiLabelPipe, SubProductModel } from 'hx-services';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { SubProductEditComponent } from '@adm-web-app/modules/composite-products/subproduct-edit/subproduct-edit.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { produce } from 'immer';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { HxBreadcrumbsComponent } from '@adm-web-app/components/breadcrumbs/breadcrumbs.component';
import { NzPageHeaderComponent, NzPageHeaderSubtitleDirective, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { HxCountrySelectComponent, HxLabelEditComponent, HxTagSelectComponent } from 'hx-components';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { NgFor } from '@angular/common';
import { NzInputNumberComponent } from 'ng-zorro-antd/input-number';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';

@Component({
    selector: 'hc-composite-products-edit',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.less'],
  imports: [TranslocoDirective, NzAffixComponent, HxBreadcrumbsComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzPageHeaderSubtitleDirective, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, HxLabelEditComponent, HxCountrySelectComponent, NzSelectComponent, NgFor, NzOptionComponent, NzInputNumberComponent, NzRadioGroupComponent, NzRadioComponent, HxTagSelectComponent, NzDividerComponent, NzButtonComponent, NzWaveDirective, NzIconDirective, NzPopconfirmDirective, HxUiLabelPipe, TranslocoPipe]
})
export class CompositeProductEditComponent implements OnInit, OnDestroy {
  product!: CompositeProductModel;
  isEdit = false;
  isCreate = false;
  form!: UntypedFormGroup;
  subProducts: SubProductModel[] = [];

  compositeTypeOptions = Object.keys(CompositeProductType).map(key => ({key: `enum.CompositeProductType.${key}`, value: key}));
  currency?: string;
  private $destroyed = new Subject<void>();

  constructor(
    private aRoute: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private modal: NzModalService,
    private productService: HxProductInfoService,
    private tr: TranslocoService,
    private notification: NzNotificationService,
    private router: Router,
  ) {
  }

  breadcrumbFn: (key: string) => string = key => this.tr.translate(`breadcrumb.${key}`);

  ngOnInit() {
    const routeData: Data = this.aRoute.snapshot.data;
    this.product = routeData['product'] as CompositeProductModel;
    if (this.product) {
      this.isEdit = true;
    } else {
      this.product = {} as CompositeProductModel;
      this.isCreate = true;
    }

    this.form = this.fb.group({
      countryId: [null, [Validators.required]],
      title: [null, [Validators.required]],
      description: [null, [Validators.required]],
      type: [CompositeProductType.SINGLE, [Validators.required]],
      sortOrder: [null],
      price: [null, [Validators.required]],
      onlineSellable: [false, [Validators.required]],
      sellable: [false, [Validators.required]],
      tags: [null]
    });
    this.form.patchValue(this.product);
    this.subProducts = this.product.subProducts || [];
  }

  addSubProduct() {
    this.modal.create({
      nzTitle: 'Продукт',
      nzContent: SubProductEditComponent,
      nzFooter: null
    }).afterClose.subscribe(result => {
      if (result) {
        this.subProducts = produce(this.subProducts, subProducts => {
          subProducts.push(result as SubProductModel);
        });
      }
    });
  }

  editSubProduct(subProduct: SubProductModel, index: number) {
    const modalRef = this.modal.create({
      nzTitle: 'Продукт',
      nzContent: SubProductEditComponent,
      nzFooter: null
    });
    if (modalRef.componentInstance) {
      modalRef.componentInstance.subProduct = subProduct;
    }
    modalRef.afterClose.subscribe(result => {
      if (result) {
        this.subProducts = produce(this.subProducts, supProducts => {
          supProducts[index] = result;
        });
      }
    });
  }

  submitForm() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid) {
      const model = this.form.value as CompositeProductModel;
      model.id = this.product.id;
      model.subProducts = this.subProducts;
      this.productService.saveCompositeProduct(model).subscribe(result => {
        this.notification.create(
          'success',
          'Композитный продукт',
          this.tr.translate('nt.dataSaved')
        );
        if (this.isCreate) {
          this.router.navigate([`/composite-products/${result.id}/edit`]);
        }
      });
    }
  }

  deleteSubProduct(ix: number) {
    this.subProducts.splice(ix, 1);
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }
}
